
import { Routes } from '../../shared/enums/app-constants';
import { LoggerService } from '@optima-stride-ai/core';
import { GlobalStateService } from '../../../service/global.service';
import { LibraryService } from '../libraries/library.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { signal, inject, Input, Component, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProcessProgressView, ProgressIndicatorComponent, ShimmerVariant } from '@optima-stride-ai/optima-components';
import { DocumentLoaderComponent } from '../../shared/components/document-loader/document-loader.component';
import { EntityLayoutConfigService } from '../../services/entity-layout-config.service';

@Component({
  selector: 'app-pdf-viewer',
  imports: [CommonModule, ProgressIndicatorComponent],
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.sass']
})
export class PdfViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() id?: string;
  private router = inject(Router);
  private libraryService = inject(LibraryService);
  private sanitizer = inject(DomSanitizer);
  private logger = inject(LoggerService);
  private entityLayoutConfigService = inject(EntityLayoutConfigService);
  private pdfEntityId = signal<string>('');
     readonly ProcessProgressView = ProcessProgressView;

  pdfName = signal<string>('');
  isLoading = signal<boolean>(true);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  pdfUrl = signal<SafeResourceUrl | null>(null);
  rawPdfUrl = signal<string>('');

  // Expose enums for template
  readonly ShimmerVariant = ShimmerVariant;

  constructor(private global: GlobalStateService) { }

  ngOnInit(): void {
    this.global.setIsPreviewOpen(true);
    if (this.id) {
      this.loadPdf();
    } else {
      setTimeout(() => {
        if (this.id) {
          this.loadPdf();
        } else {
          this.hasError.set(true);
          this.errorMessage.set('PDF ID not available - please try refreshing');
          this.isLoading.set(false);
        }
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] && changes['id'].currentValue && changes['id'].currentValue !== changes['id'].previousValue) {
      this.loadPdf();
    }
  }

  ngOnDestroy(): void {
    // Clear entity layout cache to force fresh thumbnail fetch on dashboard
    if (this.id) {
      this.entityLayoutConfigService.clearEntityCache(this.id);
    }
    
    this.global.setIsPreviewOpen(false);
  }

  private async loadPdf(): Promise<void> {
    try {
      let pdfBizEntityId = this.id;
      if (!pdfBizEntityId) {
        const sessionId = sessionStorage.getItem('pdfBizEntityId');
        pdfBizEntityId = sessionId || undefined;
      }
      if (!pdfBizEntityId) {
        this.logger.error('❌ PDF Viewer Component - No PDF ID found in input or sessionStorage');
        this.hasError.set(true);
        this.errorMessage.set('No PDF specified');
        this.isLoading.set(false);
        return;
      }
      this.hasError.set(false);
      this.errorMessage.set('');
      this.isLoading.set(true);
      this.pdfEntityId.set(pdfBizEntityId);
      this.pdfName.set(`Document-${pdfBizEntityId}`);
      const rawUrl = await this.libraryService.getDocumentUrl(pdfBizEntityId);
      this.rawPdfUrl.set(rawUrl);
      const pdfUrlWithParams = `${rawUrl}#view=FitH&toolbar=1&navpanes=1&scrollbar=1`;
      const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrlWithParams);
      this.pdfUrl.set(safeUrl);
    } catch (error) {
      this.logger.error(`❌ Error loading PDF: ${error}`);
      this.hasError.set(true);
      this.errorMessage.set(error instanceof Error ? error.message : 'Failed to load PDF');
    } finally {
      this.isLoading.set(false);
    }
  }

  retryLoad(): void {
    this.isLoading.set(true);
    this.hasError.set(false);
    this.errorMessage.set('');
    this.loadPdf();
  }

  openInNewTab(): void {
    const url = this.rawPdfUrl();
    if (url) {
      window.open(url, '_blank');
    }
  }

  goBack(): void {
    sessionStorage.removeItem('pdfBizEntityId');
    this.router.navigate([Routes.LIBRARIES]);
  }
}